import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import Loading from '@orionx-dev/orionsoft-parts/lib/components/Loading'

const getSizeClasses = size => {
  switch (size) {
    case 'small': {
      return 'px-6 py-4'
    }
    case 'large': {
      return 'px-32 py-4'
    }
    case 'full': {
      return 'w-full py-4'
    }
    default: {
      return 'px-16 py-4'
    }
  }
}

const getModeClasses = hierarchy => {
  switch (hierarchy) {
    case 'primary': {
      return `
        text-white
        disabled:text-ox-gray-600
        bg-ox-blue-800
        hover:bg-ox-purple-600
        active:bg-ox-blue-700
        disabled:bg-ox-gray-200
      `
    }
    case 'secondary': {
      return `
        text-ox-blue-800
        bg-ox-purple-300
        hover:bg-ox-purple-400
        active:bg-ox-purple-500
        disabled:bg-ox-gray-100
        disabled:text-ox-gray-600
      `
    }
    case 'outlined': {
      return `
        text-ox-blue-800
        disabled:text-ox-gray-200

        bg-transparent
        hover:bg-ox-purple-200
        active:bg-ox-purple-300
        disabled:bg-transparent
        
        border-ox-blue-800
        border-2
        disabled:border-ox-gray-200
      `
    }
    case 'tertiary': {
      return `
        text-ox-blue-800
        bg-transparent
        hover:bg-ox-purple-200
        active:bg-ox-purple-300
        disabled:text-ox-gray-200
        disabled:bg-transparent
      `
    }
    default: {
      // tertiary
      return `
        text-blue-800
        bg-transparent
        hover:bg-ox-purple-200
        active:bg-ox-purple-300
        disabled:text-ox-gray-200
        disabled:bg-transparent
      `
    }
  }
}

const BASE_BUTTON_CLASSES = `
  cursor-pointer
  rounded-lg
  font-bold
  leading-none
  inline-block
  text-lg
  font-normal
  font-space
  leading-tight
  justify-center
  flex
`

/**
 * Primary UI component for user interaction
 */
export const Button = ({
  hierarchy = 'secondary',
  size = 'medium',
  label,
  disabled = false,
  icon: Icon = null, // new prop for icon
  iconPosition = 'left', // new prop for icon position
  loading = false,
  primary = false,
  fullWidth = false,
  iconSize = 20,
  className,
  children,
  ...props
}) => {
  hierarchy = primary ? 'primary' : hierarchy
  size = fullWidth ? 'full' : size
  disabled = disabled || loading
  label = children || label
  Icon = loading ? Loading : Icon

  const computedClasses = useMemo(() => {
    const modeClass = getModeClasses(hierarchy)
    const sizeClass = getSizeClasses(size)

    return [modeClass, sizeClass].join(' ')
  }, [hierarchy, size])

  Icon = Icon && <Icon size={iconSize} />

  return (
    <button
      type="button"
      disabled={disabled}
      {...props}
      className={`${BASE_BUTTON_CLASSES} ${computedClasses} ${className}`}>
      {Icon && iconPosition === 'left' && <div className={`mr-2`}>{Icon}</div>}
      {label}
      {Icon && iconPosition === 'right' && <div className={`ml-2`}>{Icon}</div>}
    </button>
  )
}

Button.propTypes = {
  hierarchy: PropTypes.oneOf(['primary', 'secondary', 'outlined', 'tertiary']),
  size: PropTypes.oneOf(['small', 'medium', 'large', 'full']),
  label: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.any, // new propType for icon
  iconPosition: PropTypes.oneOf(['left', 'right']), // new propType for icon position
  loading: PropTypes.bool,
  primary: PropTypes.bool,
  fullWidth: PropTypes.bool,
  children: PropTypes.node,
  iconSize: PropTypes.number,
  className: PropTypes.string,
}
